/**
 * Ce que le serveur sait faire aujourd'hui.
 *
 * SoinsLib est construit par lots successifs. Le lot F a dessiné l'interface
 * entière — planning, disponibilités, facturation, tournées — contre des routes
 * que les lots C, D et E écriront plus tard. En attendant, ces écrans appellent
 * un serveur qui ne connaît pas leurs adresses et affichent un encadré rouge
 * « la demande a été refusée ».
 *
 * Ce n'est pas ce que ça veut dire. Une fonction pas encore écrite n'est pas une
 * panne, mais à l'écran les deux se ressemblent — et c'est la panne qu'on lit.
 *
 * La liste vient du serveur (`GET /modules`) et non d'une constante écrite ici :
 * une constante deviendrait fausse le jour où un lot fusionne, et personne ne
 * s'en apercevrait. Le serveur, lui, déduit la réponse des routes qu'il monte
 * réellement.
 */

import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { api } from './api/client'

export type NomModule = 'planning' | 'disponibilites' | 'facturation' | 'tournees' | 'carte'

type Modules = Record<string, boolean>

// Le fichier exporte un crochet et des composants. Les separer dans deux
// fichiers pour satisfaire le rafraichissement a chaud eloignerait la regle
// (`useModules`) de ses deux usages, qui ne se comprennent qu'ensemble.
// eslint-disable-next-line react-refresh/only-export-components
export function useModules() {
  const { data, isPending } = useQuery({
    queryKey: ['modules'],
    queryFn: () => api.get<Modules>('/modules'),
    // La réponse ne change qu'au déploiement d'une nouvelle version : la
    // redemander à chaque navigation serait du bruit.
    staleTime: Infinity,
    // Un serveur injoignable ne doit pas vider le menu : sans réponse, on
    // suppose tout disponible et l'utilisateur retombe sur le comportement
    // d'avant — un écran d'erreur, mais rien de caché à tort.
    retry: 1,
  })

  const modules = data ?? {}

  return {
    /** Vrai tant qu'on ne sait pas : on ne masque rien sur une supposition. */
    disponible: (nom: NomModule) => (isPending || !data ? true : modules[nom] === true),
    chargement: isPending,
  }
}

/**
 * Enveloppe une page dont le module peut manquer.
 *
 * Sert à ceux qui arrivent par une adresse directe ou un favori : l'entrée de
 * menu a disparu, mais la route existe toujours.
 */
export function ModuleRequis({
  nom,
  titre,
  children,
}: {
  nom: NomModule
  titre: string
  children: ReactNode
}) {
  const { disponible, chargement } = useModules()

  if (chargement) return null
  if (disponible(nom)) return <>{children}</>

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="text-xl font-semibold text-slate-900">{titre}</h1>
      <p className="mt-3 text-sm text-slate-600">
        Cette partie de SoinsLib n&apos;est pas encore ouverte sur ce serveur. Elle apparaîtra
        dans le menu dès qu&apos;elle sera disponible.
      </p>
    </div>
  )
}

/**
 * Où atterrir après la connexion.
 *
 * Le planning est l'écran d'accueil naturel — c'est le cœur du produit. Mais
 * tant que le lot C n'est pas là, y envoyer l'utilisateur le fait arriver sur
 * une page « à venir », ce qui est la pire première impression possible. On se
 * rabat alors sur les patients, qui eux fonctionnent.
 */
export function AccueilParDefaut() {
  const { disponible, chargement } = useModules()

  if (chargement) return null
  return <Navigate to={disponible('planning') ? '/planning' : '/patients'} replace />
}
